import React, { useEffect, useState } from "react";
import { register } from "../../../services/user.services";
import { toast } from "react-hot-toast";
import { updateAdmin } from "../../../services/admin.services";

const CreateRoleModal = ({ fetchUsers, isOpen, onClose, user, isEditing }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "",
    duration: "",
    superAdmin: false,
    position: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordEditable, setIsPasswordEditable] = useState(true);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isEditing && user) {
      setFormData({
        name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
        email: user.email || "",
        password: "",
        role: user.role || "",
        duration: user.duration || "",
        superAdmin: user.role === "superadmin",
        position: user.position || "",
      });
      setIsPasswordEditable(false);
    } else {
      setFormData({
        name: "",
        email: "",
        password: "",
        role: "",
        duration: "",
        superAdmin: false,
        position: "",
      });
      setIsPasswordEditable(true);
    }
  }, [user, isEditing]);

  const capitalize = (string) =>
    string
      ? string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
      : "";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
      role:
        name === "superAdmin" && checked
          ? "superadmin"
          : name === "role"
          ? value
          : prev.role,
    }));
  };

  const validatePassword = (password) => {
    if (!password.trim()) return true;
    const PASSWORD_REGEX =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&+=!]).{8,}$/;
    if (!PASSWORD_REGEX.test(password)) {
      toast.error(
        "Password must be at least 8 characters long, contain one uppercase letter, one lowercase letter, one number, and one special character."
      );
      return false;
    }
    return true;
  };

  const validateForm = () => {
    const [firstName, lastName] = formData.name.trim().split(" ");

    if (!firstName || !lastName) {
      toast.error("Please enter both first name and last name.");
      return false;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      return false;
    }

    if (isPasswordEditable && !validatePassword(formData.password)) {
      return false;
    }
    if (!formData.role && !formData.superAdmin) {
      toast.error("Please select a role or choose Super Admin.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);

    try {
      const firstName = capitalize(formData.name.split(" ")[0]);
      const lastName = capitalize(formData.name.split(" ")[1] || "");

      const data = {
        first_name: firstName,
        last_name: lastName,
        email: formData.email,
        role: formData.superAdmin ? "superadmin" : formData.role,
        position: formData.position,
        username: `${firstName} ${lastName}`,
      };

      if (formData.role === "student") {
        data.duration = formData.duration;
      }

      if (isPasswordEditable && formData.password.trim()) {
        data.password = formData.password;
      }

      if (isEditing) {
        await updateAdmin(user.admin_id, data);
        toast.success("User updated successfully!");
      } else {
        await register(data);
        toast.success("Role created successfully!");
      }

      setFormData({
        name: "",
        email: "",
        password: "",
        role: "",
        duration: "",
        superAdmin: false,
        position: "",
      });
      fetchUsers();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(
        isEditing
          ? "Failed to update user."
          : "Failed to create role. Please try with different Email"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white w-full max-w-sm sm:max-w-md lg:max-w-lg rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 rounded-t-lg">
          <div className="flex justify-between items-center">
            <h2 className="text-lg sm:text-xl font-bold text-gray-800">
              {isEditing ? "Edit Role" : "Create a Role"}
            </h2>
            <button
              className="text-gray-500 hover:text-gray-700 text-2xl font-light transition-colors p-1"
              onClick={onClose}
              type="button"
            >
              ×
            </button>
          </div>
        </div>

        {/* Form Content */}
        <div className="px-4 sm:px-6 py-4 sm:py-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
            {/* Name Field */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Enter first and last name"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Enter both first and last name
              </p>
            </div>

            {/* Email Field */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Email Address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Enter email address"
                required
              />
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Password{" "}
                {isEditing &&
                  !isPasswordEditable &&
                  "(Click 'Edit Password' to change)"}
                {!isEditing && <span className="text-red-500">*</span>}
              </label>
              <div className="space-y-2">
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                    placeholder="Enter password"
                    disabled={isEditing && !isPasswordEditable}
                    required={!isEditing}
                  />
                  {(isPasswordEditable || !isEditing) && (
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? "👁️" : "👁️‍🗨️"}
                    </button>
                  )}
                </div>

                {isEditing && (
                  <button
                    type="button"
                    onClick={() => setIsPasswordEditable(!isPasswordEditable)}
                    className="w-full sm:w-auto bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors"
                  >
                    {isPasswordEditable
                      ? "Cancel Password Edit"
                      : "Edit Password"}
                  </button>
                )}

                {(isPasswordEditable || !isEditing) && (
                  <p className="text-xs text-gray-500">
                    Minimum 8 characters with uppercase, lowercase, number, and
                    special character
                  </p>
                )}
              </div>
            </div>

            {/* Designation Field */}
            <div>
              <label className="block text-gray-700 text-sm font-medium mb-2">
                Designation <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="position"
                value={formData.position}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                placeholder="Enter job designation"
                required
              />
            </div>

            {/* Super Admin Checkbox */}
            <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
              <input
                type="checkbox"
                name="superAdmin"
                checked={formData.superAdmin}
                onChange={handleChange}
                className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500 mt-0.5"
                id="superAdmin"
              />
              <div>
                <label
                  htmlFor="superAdmin"
                  className="text-sm font-medium text-gray-700 cursor-pointer"
                >
                  Super Admin
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Super Admins have full access to all features and settings
                </p>
              </div>
            </div>

            {/* Role Selection - Hidden when Super Admin is checked */}
            {!formData.superAdmin && (
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                  required
                >
                  <option value="">Select a role</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="w-full sm:w-auto order-2 sm:order-1 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="w-full sm:flex-1 order-1 sm:order-2 bg-purple-600 text-white px-6 py-2.5 rounded-lg shadow hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </div>
                ) : isEditing ? (
                  "Update Role"
                ) : (
                  "+ Add Role"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateRoleModal;
